// src/components/wellfinder/QueueRow.jsx
//
// One row of the adjudication queue. Density over decoration: an analyst
// orienting themselves in a 10,803-record queue is scanning, and every pixel of
// padding is a row they cannot see.
//
// Deliberately dumb. Takes a row payload and three booleans, renders, calls
// onSelect. All queue state lives in useWfQueueStore; a row that reached into
// the store would re-render every sibling on any store change.

import { WF_FLAG_REASON_SHORT } from "../../constants/wfFlagReasons";
import LeaseName from "./LeaseName";

const TIER_LABEL = {
  a: "A",
  a_prime: "A′",
  b: "B",
  c: "C",
  d: "D",
};

// TIER C IS EMPHASISED ON PURPOSE. It is 164 records — 1.5% of volume — and
// roughly half the review effort, because it is the tier with no registry
// candidate at all: nothing within the search radius. Those are the detections
// that cannot be checked against any state database, so they are the only ones
// producing findings a customer could not have got elsewhere. A queue that
// renders C the same weight as A buries the work that matters.
//
// The rest are deliberately quiet. A' is distinguished from A because it is the
// bulk-confirmation candidate pool and an analyst needs to see when they have
// left it.
const TIER_CLASS = {
  c: "bg-accent-info/15 text-accent-info ring-1 ring-accent-info/40",
  b: "bg-white/5 text-fg-primary ring-1 ring-white/10",
  a_prime: "bg-white/5 text-fg-secondary ring-1 ring-white/10",
  a: "bg-white/5 text-fg-secondary ring-1 ring-white/5",
  d: "bg-white/5 text-fg-secondary ring-1 ring-white/5",
};

/** "3 min" / "just now" — claim age, not a timestamp. Nobody reads a timestamp. */
function claimAge(claimedAt) {
  if (!claimedAt) return null;
  const ms = Date.now() - new Date(claimedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  return `${min} min`;
}

function fmtDistance(m) {
  if (m == null) return null;
  // Sub-metre precision below 10m: p50 is 4.78m, so that is where the spread
  // the analyst is calibrating against actually lives. Whole metres above.
  return m < 10 ? `${m.toFixed(1)} m` : `${Math.round(m)} m`;
}

export default function QueueRow({ row, selected, isMine, onSelect }) {
  // top_candidate is rank 1, whatever was adopted. Shown only when the record
  // HAS a match: a tier B row nobody had matched used to read as rank 1's well.
  const cand = row.has_match ? row.top_candidate ?? null : null;
  const unmatched = !cand && (row.candidate_count ?? 0) > 0;

  // Occupied means ANOTHER analyst on a LIVE claim. The caller's own claim is
  // not contention — it is the analyst's own working set, and rendering it as a
  // warning would make every row they claimed look broken. An expired claim is
  // not contention either; claim_active carries the server's TTL so the client
  // never reimplements it.
  const occupied = Boolean(row.claim_active) && !isMine;
  const age = occupied ? claimAge(row.claimed_at) : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      aria-current={selected ? "true" : undefined}
      data-testid={`queue-row-${row.id}`}
      className={[
        // TWO FIXED COLUMNS AND ONE THAT GIVES. The previous row had five
        // columns on fixed bases totalling more than the pane, so at any width
        // below ~26rem the identity text painted straight over the distance and
        // the operator over the claim. Fixed bases are now only for content of
        // known width (a tier badge, a formatted distance) and everything with
        // unbounded text lives in the one minmax(0,1fr) column and truncates.
        //
        // minmax(0,...) rather than 1fr: a grid track defaults to min-content,
        // which refuses to shrink below the longest word, which is exactly how
        // the overlap happened. This is the half of the pair that was missing —
        // the other half is min-w-0 on each flex child below.
        "w-full text-left grid items-start gap-x-2 px-2.5 py-1.5",
        "grid-cols-[1.75rem_minmax(0,1fr)_5.25rem]",
        "border-b border-bg-border text-xs",
        selected ? "bg-accent-action/15" : "hover:bg-white/[0.03]",
      ].join(" ")}
    >
      {/* Tier */}
      <span
        className={[
          "justify-self-start rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
          TIER_CLASS[row.tier] ?? TIER_CLASS.a,
        ].join(" ")}
      >
        {TIER_LABEL[row.tier] ?? row.tier}
      </span>

      {/* Identity. Two lines for a row with a candidate, ONE for tier C.
          Nothing is dropped at narrow widths — it truncates, in priority order:
          the lease name survives longest, the operator gives way first. */}
      <span className="min-w-0">
        {cand ? (
          <>
            <span className="flex min-w-0 items-baseline gap-1">
              <span className="truncate text-fg-primary">
                <LeaseName value={cand.lease_name} source={cand.lease_name_source} />
                {cand.well_no ? ` ${cand.well_no}` : ""}
              </span>
              {cand.operator && (
                <span className="min-w-0 truncate text-fg-secondary/80">
                  · {cand.operator}
                </span>
              )}
            </span>
            <span className="flex min-w-0 items-baseline gap-1.5 text-[11px] text-fg-secondary/70">
              {/* Monospace so digit positions line up down the stack. See the
                  candidate panel for why: these differ in interior digits. */}
              <span className="font-mono tabular-nums">{cand.api}</span>
              {row.candidate_count > 1 && <span>· {row.candidate_count} cand</span>}
              {occupied && (
                <span className="min-w-0 truncate text-amber-300/90">
                  · {row.claimed_by_name || "another analyst"}
                  {age ? ` ${age}` : ""}
                </span>
              )}
              {/* SHORT form. The full sentence-length label is what a
                  supervisor reads once they open the record; here it competes
                  with the API and the candidate count for one truncating
                  column, and a wrapped row costs more than the words buy. */}
              {row.flag_reason && (
                <span className="min-w-0 truncate text-amber-300/90">
                  · {WF_FLAG_REASON_SHORT[row.flag_reason] ?? row.flag_reason}
                </span>
              )}
            </span>
          </>
        ) : unmatched ? (
          // One line, as tier C's below. Approved with no match is the
          // reviewer's "none of these is the well"; otherwise nothing is adopted yet.
          <span className="block min-w-0 truncate italic text-fg-secondary">
            {row.status === "approved"
              ? "no matching RRC record"
              : `no candidate adopted · ${row.candidate_count} cand`}
            {occupied && (
              <span className="not-italic text-amber-300/90">
                {" · "}{row.claimed_by_name || "another analyst"}
                {age ? ` ${age}` : ""}
              </span>
            )}
            {row.flag_reason && (
              <span className="not-italic text-amber-300/90">
                {" · "}{WF_FLAG_REASON_SHORT[row.flag_reason] ?? row.flag_reason}
              </span>
            )}
          </span>
        ) : (
          // ONE LINE. "no registry well nearby" wrapped to four lines in a
          // narrow pane, which is the same defect inverted: 164 tier C rows at
          // four lines each destroys the scannability the tier most needs. The
          // C badge already carries the emphasis, so the text can be short.
          <span className="block min-w-0 truncate italic text-fg-secondary">
            no registry match
            {row.flag_reason && (
              <span className="not-italic text-amber-300/90">
                {" · "}{WF_FLAG_REASON_SHORT[row.flag_reason] ?? row.flag_reason}
              </span>
            )}
          </span>
        )}
      </span>

      {/* Distance and status. Fixed 5.25rem because the content is formatted to
          a known width — "42 m NNE" is the longest it gets. */}
      <span className="justify-self-end text-right tabular-nums text-fg-secondary">
        {cand ? (
          <>
            <span className="block">
              {fmtDistance(cand.distance_m)}
              {cand.bearing ? ` ${cand.bearing}` : ""}
            </span>
            <span className="block text-[11px] text-fg-secondary/60">{row.status}</span>
          </>
        ) : (
          <span className="block text-[11px] text-fg-secondary/60">{row.status}</span>
        )}
      </span>
    </button>
  );
}
